import React, { useState, useEffect } from 'react';
import { Button, Box } from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';

const AnimatedTravelButton = ({ onClick, sx = {}, children = "New Travel Plan", ...props }) => {
  const [isHovered, setIsHovered] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  // Available cover images
  const coverImages = [
    '/covers/adventure.png',
    '/covers/city.png', 
    '/covers/food_shopping.png'
  ];

  // Cycle through images while hovering
  useEffect(() => {
    let interval;
    if (isHovered) {
      interval = setInterval(() => {
        setCurrentImageIndex((prevIndex) => 
          (prevIndex + 1) % coverImages.length
        );
      }, 500); // Change image every 500ms - fast flip (0.15s) + display time (0.35s)
    }
    
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isHovered, coverImages.length]);

  return (
    <Box
      sx={{
        position: 'relative',
        display: 'inline-block',
        ...sx
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => {
        setIsHovered(false);
        setCurrentImageIndex(0); // Reset to first image when not hovering
      }}
    >
      {/* Button */}
      <Button
        variant="contained"
        startIcon={<AddIcon />}
        onClick={onClick}
        sx={{
          borderRadius: 4,
          bgcolor: 'transparent',
          color: '#702963',
          position: 'relative',
          overflow: 'hidden',
          zIndex: 2,
          transition: 'all 0.3s ease',
          border: '2px solid rgba(255, 255, 255, 0.3)',
          textShadow: '0 1px 3px rgba(0,0,0,0.8)',
          '& .MuiButton-startIcon': {
            filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.8))',
            color: '#702963'
          },
          '&:hover': {
            bgcolor: 'transparent',
            transform: 'scale(1.05)',
            border: '2px solid rgba(255, 255, 255, 0.5)',
            color: 'white',
            textShadow: '0 2px 4px rgba(0,0,0,0.9)',
            '& .MuiButton-startIcon': {
              filter: 'drop-shadow(0 2px 3px rgba(0,0,0,0.9))',
              color: 'white'
            }
          }
        }}
        {...props}
      >
        {children}
      </Button>

      {/* Animated Book Pages */}
      {isHovered && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 1,
            borderRadius: 4,
            overflow: 'hidden',
            perspective: '1000px'
          }}
        >
          {coverImages.map((image, index) => (
            <Box
              key={image}
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                                 backgroundImage: `url(${image})`,
                 backgroundSize: 'cover',
                 backgroundPosition: 'center',
                 borderRadius: 4,
                transform: currentImageIndex === index 
                  ? 'rotateY(0deg) translateZ(0px)' 
                  : currentImageIndex > index
                    ? 'rotateY(-180deg) translateZ(-20px)'
                    : 'rotateY(180deg) translateZ(-20px)',
                                 transformOrigin: 'left center',
                 transition: 'transform 0.15s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                 opacity: currentImageIndex === index ? 0.8 : 0.4,
                 filter: currentImageIndex === index ? 'blur(0px)' : 'blur(2px)',
                transformStyle: 'preserve-3d',
                backfaceVisibility: 'hidden',
                boxShadow: currentImageIndex === index 
                  ? '0 10px 30px rgba(0,0,0,0.3)' 
                  : '0 5px 15px rgba(0,0,0,0.2)',
                                 animation: currentImageIndex === index 
                   ? 'pageFlip 0.15s ease-out' 
                   : 'none',
                '@keyframes pageFlip': {
                  '0%': {
                    transform: 'rotateY(180deg) translateZ(-20px)',
                    opacity: 0.4
                  },
                  '50%': {
                    transform: 'rotateY(90deg) translateZ(10px)',
                    opacity: 0.6
                  },
                  '100%': {
                    transform: 'rotateY(0deg) translateZ(0px)',
                    opacity: 0.8
                  }
                }
              }}
            />
          ))}
          
          {/* Overlay gradient to ensure text readability */}
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'linear-gradient(45deg, rgba(0,0,0,0.3), rgba(255,255,255,0.1))',
              borderRadius: 4,
              zIndex: 1
            }}
          />
        </Box>
      )}
    </Box>
  );
};

export default AnimatedTravelButton; 